import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import {
  splitOrders,
  useDrfqOrdersFetcher,
  useDrfqTradeTapeFetcher,
  useSplittedOrders,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/hooks';
import {
  Drfq,
  REPRICE_TIMEOUT_MS,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { useDrfqService } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { toast } from '#/components/UnifiedRfqs/toast';
import { TradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/shared';
import Timer from '#/components/UnifiedRfqs/components/Timer';
import { useFormattedDrfqMakerPrice } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { primaryProductCode } from '@paradigm/features/src/unified-rfqs/utils/legs';
import { PriceButton, QtyButton } from '../../rfq-common/ActionButtons';
import { ActionButton as ActionButtonBase } from '../components/ActionButton';
import { BaseRfqAction } from '../rfqs/action/BaseRfqAction';
import { BoughtLabel, QuoteActionContainer, SoldLabel } from './common';
import TakerDesk from './TakerDesk';

export function MakerRepriceRowAction() {
  const drfq = useDrfqRfq();
  const { id: rfqId, closed_reason: closedReason } = drfq;
  const { isLoading: isLoadingOrders, orders } = useDrfqOrdersFetcher(rfqId);
  const { trade, isLoading: isLoadingTradeTape } =
    useDrfqTradeTapeFetcher(rfqId);
  const { bids, asks, executedAsk, executedBid } = splitOrders(orders);
  const isBought = !isLoadingOrders && executedBid != null;
  const isSold = !isLoadingOrders && executedAsk != null;
  const isExpired = closedReason === 'EXPIRED';
  const isCanceled = closedReason === 'CANCELED_BY_CREATOR';
  const isTradedAway =
    !isLoadingTradeTape &&
    !isLoadingOrders &&
    !isBought &&
    !isSold &&
    closedReason === 'EXECUTION_LIMIT';
  const isBoughtAway = isTradedAway && trade?.side === 'BUY';
  const isSoldAway = isTradedAway && trade?.side === 'SELL';
  const bid = isLoadingOrders ? null : isSoldAway ? trade : bids[0];
  const ask = isLoadingOrders ? null : isBoughtAway ? trade : asks[0];
  const takerDesk = <TakerDesk />;
  const timer = <Timer duration={REPRICE_TIMEOUT_MS / 1000} />;
  const buyTimer = (
    <TimerContainer data-testid="buy-timer">{timer}</TimerContainer>
  );
  const sellTimer = (
    <TimerContainer data-testid="sell-timer">{timer}</TimerContainer>
  );

  return (
    <QuoteActionContainer>
      <RepriceContainer side="BUY">
        {isBought || isExpired || isCanceled || isSoldAway
          ? buyTimer
          : takerDesk}
        {isBought && <BoughtLabel />}
        {isExpired && <ExpiredLabel />}
        {isCanceled && <CanceledLabel />}
        {isSoldAway && <TradedAwayLabel data-testid="buy-traded-away" />}
      </RepriceContainer>
      <RfqAction
        bid={bid}
        ask={ask}
        isBought={isBought}
        isSold={isSold}
        drfq={drfq}
      />
      <RepriceContainer side="SELL">
        {isExpired && <ExpiredLabel />}
        {isCanceled && <CanceledLabel />}
        {isBoughtAway && <TradedAwayLabel data-testid="sell-traded-away" />}
        {isBought && <span />}
        {isSoldAway && <span />}
        {isSold && <SoldLabel />}
        {isSold || isBoughtAway ? sellTimer : takerDesk}
      </RepriceContainer>
    </QuoteActionContainer>
  );
}

export function TakerRepriceRowAction() {
  const drfq = useDrfqRfq();
  const { id: rfqId, closed_reason: closedReason } = drfq;
  const { bids, asks, executedAsk, executedBid } = useSplittedOrders(rfqId);
  const isBought = executedAsk != null;
  const isSold = executedBid != null;
  const bid = asks[0];
  const ask = bids[0];
  const isExpired = closedReason === 'EXPIRED';
  const isCanceled = closedReason === 'CANCELED_BY_CREATOR';
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const repriceButton = <RepriceButton disabled={isPlatformCreationDisabled} />;

  return (
    <QuoteActionContainer>
      <RepriceContainer side="BUY" css="padding-left: 0;">
        {(isBought || isExpired || isCanceled) && repriceButton}
        {isBought && <SoldLabel />}
        {isExpired && <ExpiredLabel />}
        {isCanceled && <CanceledLabel />}
      </RepriceContainer>
      <RfqAction
        isBought={isBought}
        isSold={isSold}
        bid={bid}
        ask={ask}
        drfq={drfq}
      />
      <RepriceContainer side="SELL" css="padding-right: 0;">
        {isCanceled && <CanceledLabel />}
        {isExpired && <ExpiredLabel />}
        {isSold && <BoughtLabel />}
        {isSold && repriceButton}
      </RepriceContainer>
    </QuoteActionContainer>
  );
}

interface RfqActionProps {
  readonly isBought: boolean;
  readonly isSold: boolean;
  readonly bid?: Order | TradeTapeItem | null;
  readonly ask?: Order | TradeTapeItem | null;
  readonly drfq: Drfq;
}

function RfqAction({ bid, ask, isBought, isSold, drfq }: RfqActionProps) {
  // product_codes is ordered by leg, so its first entry need not be the
  // primary (ratio-1) leg's product once a package spans several.
  const productCode = primaryProductCode(drfq.legs) ?? drfq.product_codes[0];
  const isDated = isDatedRfqLegs(drfq.legs, productCode);

  const formattedBidPrice = useFormattedDrfqMakerPrice({
    price: bid?.price,
    side: 'BUY',
    // quote prices passed here are always from MAKER perspective
    drfqInfo: { ...drfq, role: 'MAKER' },
  });

  const formattedAskPrice = useFormattedDrfqMakerPrice({
    price: ask?.price,
    side: 'SELL',
    // quote prices passed here are always from MAKER perspective
    drfqInfo: { ...drfq, role: 'MAKER' },
  });

  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();

  return (
    <BaseRfqAction
      bidQty={
        <QtyButton
          disabled={isPlatformCreationDisabled}
          quantity={bid?.quantity ?? null}
          productCode={productCode}
          isTextMuted={!isBought}
          isNormalized
          isDated={isDated}
        />
      }
      bidPrice={
        <PriceButton
          disabled={isPlatformCreationDisabled}
          isTextMuted={!isBought}
        >
          {formattedBidPrice}
        </PriceButton>
      }
      askQty={
        <QtyButton
          disabled={isPlatformCreationDisabled}
          quantity={ask?.quantity ?? null}
          productCode={productCode}
          isTextMuted={!isSold}
          isNormalized
          isDated={isDated}
        />
      }
      askPrice={
        <PriceButton
          disabled={isPlatformCreationDisabled}
          isTextMuted={!isSold}
        >
          {formattedAskPrice}
        </PriceButton>
      }
    />
  );
}

function RepriceButton({ disabled }: { disabled: boolean }) {
  const { id } = useDrfqRfq();
  const drfqService = useDrfqService();
  const [isDisabled, setIsDisabled] = useState(false);
  const text = isDisabled ? 'Re-pricing' : 'Re-price';
  const track = useTrackAction();
  const timer = !isDisabled && (
    <Timer duration={REPRICE_TIMEOUT_MS / 1000} isColorAnimated />
  );
  const repriceRfq = useCallback(() => {
    setIsDisabled(true);
    track('reprice rfq');
    drfqService
      .repriceRfq(id)
      .then((resp) => {
        if (!resp.ok) {
          toast(resp.message);
        }
      })
      .catch(() => {
        toast('Could not reprice RFQ');
      })
      .finally(() => {
        if (!isDisabled) setIsDisabled(false);
      });
  }, [drfqService, id, isDisabled, track]);

  useEffect(() => {
    return () => setIsDisabled(false);
  }, []);

  return (
    <ActionButton
      ariaLabel="reprice rfq"
      onClick={repriceRfq}
      disabled={isDisabled || disabled}
    >
      {text} {timer}
    </ActionButton>
  );
}

const RepriceContainer = styled.div<{ side: Order['side'] }>`
  display: grid;
  align-items: center;
  grid-template-columns: ${({ side }) =>
    side === 'BUY' ? 'auto 50px' : '50px auto'};
  grid-template-areas: ${({ side }) =>
    side === 'BUY' ? '"button label"' : '"label button"'};
  text-align: center;
  padding: 0 6px;
  column-gap: 6px;

  svg {
    width: 13px;
    height: 13px;
  }
`;

const ActionButton = styled(ActionButtonBase)`
  width: auto;
  color: ${paradigmLightBlue};
  grid-area: button;
  padding: 0;
  gap: 2px;
`;

const ExpiredLabel = styled(SoldLabel).attrs({ children: 'Expired' })`
  color: white;
`;

const CanceledLabel = styled(ExpiredLabel).attrs({ children: 'Canceled' })``;

const TradedAwayLabel = styled(ExpiredLabel).attrs({ children: 'Traded Away' })`
  white-space: pre-line;
`;

const TimerContainer = styled.div`
  text-align: center;
`;
